import React, { useEffect, useState, useRef } from 'react';
import { restaurantConfig } from '../config/restaurantConfig';

interface FooterProps {
  language: string;
}

export const Footer = ({ language }: FooterProps) => {
  const { address, phone, email } = restaurantConfig;
  const googleMapsUrl = "https://maps.google.com"; // Update with the real Google Maps URL for No Patio
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const flowersRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerPosition = footer.getBoundingClientRect();
        const isVisible = footerPosition.top < window.innerHeight;
        
        // Only update if visibility changes
        setIsVisible(prevVisible => {
          if (prevVisible !== isVisible) {
            // Reset flower animations when coming back into view
            if (isVisible && flowersRef.current) {
              const flowers = flowersRef.current.querySelectorAll('.garden-flower');
              flowers.forEach(flower => {
                (flower as HTMLElement).style.animation = 'none';
                // Reset particle animations as well
                const particles = flower.querySelectorAll('.flower__particle');
                particles.forEach(particle => {
                  (particle as HTMLElement).style.animation = 'none';
                });
                
                setTimeout(() => {
                  if (flower.className.includes('garden-flower--1')) {
                    (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-1 4s linear infinite';
                  } else if (flower.className.includes('garden-flower--2')) {
                    (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-2 4s linear infinite';
                  } else if (flower.className.includes('garden-flower--3')) {
                    (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-3 4s linear infinite';
                  }
                  
                  // Restart particle animations
                  particles.forEach((particle, index) => {
                    const delay = 0.3 + (index * 0.2);
                    (particle as HTMLElement).style.animation = `particle-float 3s ${delay}s ease-in-out infinite`;
                  });
                }, 50);
              });
            }
          }
          return isVisible;
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <footer className="bg-white text-black py-12 relative overflow-hidden">
      {/* Decorative Vine at Top of Footer */}
      <div className="absolute top-0 left-0 w-full h-8 overflow-hidden">
        <div className="w-full h-6 bg-white relative">
          <div className="absolute -top-5 left-0 w-full">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 100" className="w-full">
              <path fill="#4c8a5a" fillOpacity="0.5" d="M0,32L80,37.3C160,43,320,53,480,58.7C640,64,800,64,960,58.7C1120,53,1280,43,1360,37.3L1440,32L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"></path>
            </svg>
          </div>
        </div>
      </div>
      
      {/* Footer Birds Animation - Horizontal flights only, maximum 4 */}
      <div className="footer-bird-container footer-bird-container-one">
        <div className="bird footer-bird-one"></div>
      </div>
      <div className="footer-bird-container footer-bird-container-two">
        <div className="bird footer-bird-two"></div>
      </div>
      <div className="footer-bird-container footer-bird-container-three">
        <div className="bird footer-bird-three"></div>
      </div>
      <div className="footer-bird-container footer-bird-container-four">
        <div className="bird footer-bird-four"></div>
      </div>
    
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="footer-logo">
            <div 
              className="cursor-pointer" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img src="/images/logo.png" alt="No Patio" className="h-32 mb-4" />
              <p className="text-gray-800 font-medium">
                {language === 'en' 
                  ? "Dine in the Garden"
                  : "Refeições no Jardim"
                }
              </p>
            </div>
          </div>
          
          <div className="footer-contact">
            <h3 className="text-xl font-bold mb-4 text-[var(--color-pink)]">
              {language === 'en' ? 'Contact Us' : 'Contate-nos'}
            </h3>
            <p className="mb-2 text-gray-800">
              <a 
                href={googleMapsUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[var(--color-pink)] transition-colors flex items-center"
              >
                <i className="fas fa-map-marker-alt mr-2"></i>
                {language === 'en' ? address.en : address.pt}
              </a>
            </p>
            <p className="mb-2 text-gray-800">
              <a 
                href={`tel:${phone}`}
                className="hover:text-[var(--color-pink)] transition-colors flex items-center"
              >
                <i className="fas fa-phone mr-2"></i>
                {phone}
              </a>
            </p>

            <p className="text-gray-800">
              <a 
                href={`mailto:${email}`}
                className="hover:text-[var(--color-pink)] transition-colors flex items-center"
              >
                <i className="fas fa-envelope mr-2"></i>
                {email}
              </a>
            </p>
          </div>
          
          <div className="footer-hours">
            <h3 className="text-xl font-bold mb-4 text-[var(--color-pink)]">
              {language === 'en' ? 'Opening Hours' : 'Horário de Funcionamento'}
            </h3>
            {restaurantConfig.businessHours[language === 'en' ? 'en' : 'pt'].map((hours, index) => (
              <p key={index} className="mb-2 text-gray-800">
                {hours}
              </p>
            ))}
          </div>
        </div>
        
        <div className="border-t border-gray-300 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="social-links flex space-x-4 mb-4 md:mb-0">
            <a href={restaurantConfig.social.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-[var(--color-pink)] transition-colors social-icon">
              <i className="fab fa-instagram"></i>
            </a>
            <a href={restaurantConfig.social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-[var(--color-pink)] transition-colors social-icon">
              <i className="fab fa-facebook"></i>
            </a>
            <a href={restaurantConfig.social.tripadvisor} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-[var(--color-pink)] transition-colors social-icon">
              <i className="fas fa-globe"></i>
            </a>
            <a href={restaurantConfig.social.thefork} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-[var(--color-pink)] transition-colors social-icon">
              <i className="fa fa-utensils"></i>
            </a>
          </div>
          
          <div className="relative pb-16 text-center md:text-right">
            <p className="text-gray-800 text-sm mb-2">
              {language === 'en' 
                ? "© 2025 No Patio. All rights reserved."
                : "© 2025 No Patio. Todos os direitos reservados."
              }
            </p>
            <p className="text-sm">
              <a 
                href="https://www.nexosolutions.co/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
                style={{ textDecoration: 'underline' }}
              >
                Built by Nexo Solutions
              </a>
            </p>
          </div>
        </div>
      </div>
      
      {/* Garden Layer - Only flowers, no grass */}
      <div className="garden-layer">
        {/* Flowers container - visibility controlled by scroll */}
        <div 
          ref={flowersRef}
          className={`flowers-container ${isVisible ? 'visible' : 'hidden'}`}
        >
          {/* Generate flowers - fewer on mobile */}
          {[...Array(isMobile ? 23 : 70)].map((_, i) => (
            <div key={`flower-${i}`} className={`garden-flower garden-flower--${i % 3 + 1}`} style={{
              left: `${Math.random() * 100}%`,
              bottom: '0px', // Fixed position at bottom instead of random height
              animationDelay: `${0.1 + Math.random() * 0.9}s`,
              zIndex: Math.floor(Math.random() * 5) + 5
            }}>
              <div className="flower__leafs flower__leafs--1">
                <div className="flower__leaf flower__leaf--1"></div>
                <div className="flower__leaf flower__leaf--2"></div>
                <div className="flower__leaf flower__leaf--3"></div>
                <div className="flower__leaf flower__leaf--4"></div>
                <div className="flower__white-circle"></div>
                
                {/* Add just a few small particles */}
                <div className="flower__particle flower__particle--1"></div>
                <div className="flower__particle flower__particle--2"></div>
                <div className="flower__particle flower__particle--3"></div>
              </div>
              <div className="flower__line">
                <div className="flower__line__leaf flower__line__leaf--1"></div>
                <div className="flower__line__leaf flower__line__leaf--2"></div>
                <div className="flower__line__leaf flower__line__leaf--3"></div>
                <div className="flower__line__leaf flower__line__leaf--4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bird Silhouette Decoration */}
      <div className="absolute bottom-10 right-10 opacity-30">
        <svg width="50" height="30" viewBox="0 0 100 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20,30 Q40,5 60,30 Q80,55 100,30 M20,30 Q40,55 60,30" stroke="black" strokeWidth="2" fill="none" />
        </svg>
      </div>

      {/* Garden and Animation CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Bird Animation in Footer */
          .bird {
            background-image: url('https://s3-us-west-2.amazonaws.com/s.cdpn.io/174479/bird-cells-new.svg');
            background-size: auto 100%;
            width: 88px;
            height: 125px;
            will-change: background-position;
            animation-name: fly-cycle;
            animation-timing-function: steps(10);
            animation-iteration-count: infinite;
          }
          
          /* Individual Footer Birds */
          .footer-bird-one {
            animation-duration: 1s;
            animation-delay: -0.5s;
            filter: brightness(0) saturate(100%) invert(32%) sepia(15%) saturate(2867%) hue-rotate(169deg) brightness(114%) contrast(100%);
            opacity: 0.8;
          }
          
          .footer-bird-two {
            animation-duration: 1.2s;
            animation-delay: -0.25s;
            filter: brightness(0) saturate(100%) invert(32%) sepia(15%) saturate(2867%) hue-rotate(169deg) brightness(114%) contrast(100%);
            opacity: 0.8;
          }
          
          .footer-bird-three {
            animation-duration: 0.9s;
            animation-delay: -0.4s;
            filter: brightness(0) saturate(100%) invert(56%) sepia(68%) saturate(5335%) hue-rotate(314deg) brightness(100%) contrast(96%);
            opacity: 0.8;
          }
          
          .footer-bird-four {
            animation-duration: 1.1s;
            animation-delay: -0.6s;
            filter: brightness(0) saturate(100%) invert(56%) sepia(68%) saturate(5335%) hue-rotate(314deg) brightness(100%) contrast(96%);
            opacity: 0.8;
          }

          /* Footer Container animations - Horizontal flights only */
          .footer-bird-container {
            position: absolute;
            transform: scale(0) translateX(-10vw);
            will-change: transform;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            z-index: 1; /* Changed from -1 to 1 to make birds visible but still behind content with z-10 */
          }
          
          .footer-bird-container-one {
            left: -3%;
            animation-name: footer-fly-right;
            animation-duration: 25s;
            animation-delay: 2s;
            top: 20px;
          }
          
          .footer-bird-container-two {
            left: -3%;
            animation-name: footer-fly-right-curved;
            animation-duration: 35s;
            animation-delay: 15s;
            top: 60px;
          }
          
          .footer-bird-container-three {
            left: -3%;
            animation-name: footer-fly-right-wavy;
            animation-duration: 30s;
            animation-delay: 8s;
            top: 40px;
          }
          
          .footer-bird-container-four {
            right: -3%;
            animation-name: footer-fly-left;
            animation-duration: 28s;
            animation-delay: 5s;
            top: 30px;
          }
                    
          @keyframes fly-cycle {
            100% {
              background-position: -900px 0;
            }
          }
          
          /* Footer birds flight patterns - Horizontal with slight angles */
          @keyframes footer-fly-right {
            0% {
              transform: scale(0.25) translateX(-10vw);
            }
            
            10% {
              transform: translateY(1vh) translateX(10vw) scale(0.35);
            }
            
            20% {
              transform: translateY(0vh) translateX(30vw) scale(0.35);
            }
            
            30% {
              transform: translateY(2vh) translateX(50vw) scale(0.35);
            }
            
            40% {
              transform: translateY(1vh) translateX(70vw) scale(0.35);
            }
            
            50% {
              transform: translateY(0vh) translateX(90vw) scale(0.35);
            }
            
            60% {
              transform: translateY(0vh) translateX(110vw) scale(0.35);
            }
            
            100% {
              transform: translateY(0vh) translateX(110vw) scale(0.35);
            }
          }
          
          /* Left to right flight pattern */
          @keyframes footer-fly-left {
            0% {
              transform: scale(0.25) rotateY(180deg) translateX(-10vw);
            }
            
            10% {
              transform: translateY(2vh) translateX(-10vw) rotateY(180deg) scale(0.35);
            }
            
            20% {
              transform: translateY(0vh) translateX(-30vw) rotateY(180deg) scale(0.35);
            }
            
            30% {
              transform: translateY(3vh) translateX(-50vw) rotateY(180deg) scale(0.35);
            }
            
            40% {
              transform: translateY(1vh) translateX(-70vw) rotateY(180deg) scale(0.35);
            }
            
            50% {
              transform: translateY(0vh) translateX(-90vw) rotateY(180deg) scale(0.35);
            }
            
            60% {
              transform: translateY(2vh) translateX(-110vw) rotateY(180deg) scale(0.35);
            }
            
            100% {
              transform: translateY(2vh) translateX(-110vw) rotateY(180deg) scale(0.35);
            }
          }
          
          /* Curved flight pattern for footer */
          @keyframes footer-fly-right-curved {
            0% {
              transform: scale(0.25) translateX(-10vw) translateY(0vh);
            }
            
            10% {
              transform: translateY(-2vh) translateX(10vw) scale(0.30);
            }
            
            20% {
              transform: translateY(-4vh) translateX(30vw) scale(0.30);
            }
            
            30% {
              transform: translateY(-2vh) translateX(50vw) scale(0.30);
            }
            
            40% {
              transform: translateY(2vh) translateX(70vw) scale(0.30);
            }
            
            50% {
              transform: translateY(4vh) translateX(90vw) scale(0.30);
            }
            
            60% {
              transform: translateY(2vh) translateX(110vw) scale(0.30);
            }
            
            100% {
              transform: translateY(0vh) translateX(110vw) scale(0.30);
            }
          }
          
          /* Wavy flight pattern for footer */
          @keyframes footer-fly-right-wavy {
            0% {
              transform: scale(0.28) translateX(-10vw) translateY(2vh);
            }
            
            10% {
              transform: translateY(3vh) translateX(10vw) scale(0.32);
            }
            
            20% {
              transform: translateY(0vh) translateX(25vw) scale(0.32);
            }
            
            30% {
              transform: translateY(-3vh) translateX(40vw) scale(0.32);
            }
            
            40% {
              transform: translateY(0vh) translateX(55vw) scale(0.32);
            }
            
            50% {
              transform: translateY(3vh) translateX(70vw) scale(0.32);
            }
            
            60% {
              transform: translateY(0vh) translateX(85vw) scale(0.32);
            }
            
            70% {
              transform: translateY(-3vh) translateX(100vw) scale(0.32);
            }
            
            100% {
              transform: translateY(0vh) translateX(110vw) scale(0.32);
            }
          }
          
          /* Make sure birds are visible on all screen sizes */
          @media (min-width: 1024px) {
            .footer-bird-container {
              display: block !important;
            }
            
            .footer-bird-container-one {
              top: 30px;
            }
            
            .footer-bird-container-two {
              top: 80px;
            }
            
            .footer-bird-container-three {
              top: 50px;
            }
            
            .footer-bird-container-four {
              top: 40px;
            }
          }
          
          /* Garden Styles */
          .garden-layer {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 80px; /* Reduced height for flowers only */
            z-index: 1;
            overflow: hidden; /* Prevent flowers from showing outside garden layer */
          }
          
          .flowers-container {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.8s ease-in-out, visibility 0s linear 0.8s;
          }
          
          .flowers-container.visible {
            opacity: 1;
            visibility: visible;
            transition: opacity 0.8s ease-in-out, visibility 0s linear;
          }
          
          .flowers-container.hidden .garden-flower {
            transform: translateY(100%);
            opacity: 0;
          }
          
          .garden-flower {
            position: absolute;
            transform-origin: bottom center;
            --fl-speed: 0.8s;
            animation: grow-from-bottom 1.2s forwards;
            transition: opacity 0.8s ease-out, transform 0.8s ease-out;
          }
          
          .garden-flower--1 {
            animation: grow-from-bottom 1.2s forwards, moving-flower-1 4s linear infinite;
          }
          
          .garden-flower--2 {
            animation: grow-from-bottom 1.2s forwards, moving-flower-2 4s linear infinite;
          }
          
          .garden-flower--3 {
            animation: grow-from-bottom 1.2s forwards, moving-flower-3 4s linear infinite;
          }

          .flower__leafs {
            position: relative;
            animation: blooming-flower 2s backwards;
          }
          
          .flower__leafs--1 {
            animation-delay: 0.8s;
          }
          
          .flower__leafs--2 {
            animation-delay: 1.1s;
          }
          
          .flower__leafs--3 {
            animation-delay: 1.4s;
          }
          
          .flower__leafs::after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            transform: translate(-50%, -100%);
            width: 8px;
            height: 8px;
            background-color: rgba(197, 94, 134, 0.3);
            filter: blur(10px);
          }
          
          .flower__leaf {
            position: absolute;
            bottom: 0;
            left: 50%;
            width: 8px;
            height: 11px;
            border-radius: 51% 49% 47% 53% / 44% 45% 55% 69%;
            background-color: #C55E86;
            background-image: linear-gradient(to top, #a33b65, #C55E86);
            transform-origin: bottom center;
            opacity: 0.9;
            box-shadow: inset 0 0 2px rgba(255, 255, 255, 0.5);
          }
          
          .flower__leaf--1 {
            transform: translate(-10%, 1%) rotateY(40deg) rotateX(-50deg);
          }
          
          .flower__leaf--2 {
            transform: translate(-50%, -4%) rotateX(40deg);
          }
          
          .flower__leaf--3 {
            transform: translate(-90%, 0%) rotateY(45deg) rotateX(50deg);
          }
          
          .flower__leaf--4 {
            width: 8px;
            height: 8px;
            transform-origin: bottom left;
            border-radius: 4px 10px 4px 4px;
            transform: translate(-0%, 18%) rotateX(70deg) rotate(-43deg);
            background-image: linear-gradient(to top, #a33b65, #C55E86);
            z-index: 1;
            opacity: 0.8;
          }
          
          .flower__white-circle {
            position: absolute;
            left: -3.5px;
            top: -3px;
            width: 9px;
            height: 4px;
            border-radius: 50%;
            background-color: #fff;
          }
          
          .flower__white-circle::after {
            content: "";
            position: absolute;
            left: 50%;
            top: 45%;
            transform: translate(-50%, -50%);
            width: 60%;
            height: 60%;
            border-radius: inherit;
            background-image: linear-gradient(90deg, #F9D423, #F9D423);
          }
          
          .flower__line {
            height: 20px; /* Reduce from 35px */
            width: 1.5px;
            background-image: linear-gradient(
                to left,
                rgb(0, 0, 0, 0.2),
                transparent,
                rgba(255, 255, 255, 0.2)
              ),
              linear-gradient(to top, transparent 10%, #4c8a5a, #69b07d);
            box-shadow: inset 0 0 2px rgba(0, 0, 0, 0.5);
            animation: grow-flower-tree 4s backwards;
          }
          
          .garden-flower--1 .flower__line {
            height: 25px; /* Reduce from 40px */
            animation-delay: 0.3s;
          }
          
          .garden-flower--2 .flower__line {
            height: 18px; /* Reduce from 30px */
            animation-delay: 0.6s;
          }
          
          .garden-flower--3 .flower__line {
            height: 22px; /* Reduce from 35px */
            animation-delay: 0.9s;
          }
          
          .flower__line__leaf {
            --w: 7px;
            --h: calc(var(--w) + 2px);
            position: absolute;
            top: 20%;
            left: 90%;
            width: var(--w);
            height: var(--h);
            border-top-right-radius: var(--h);
            border-bottom-left-radius: var(--h);
            background-image: linear-gradient(
              to top,
              rgba(76, 138, 90, 0.6),
              #69b07d
            );
          }
          
          .flower__line__leaf--1 {
            transform: rotate(70deg) rotateY(30deg);
            animation: blooming-leaf-right var(--fl-speed) 1.6s backwards;
          }
          
          .flower__line__leaf--2 {
            top: 45%;
            transform: rotate(70deg) rotateY(30deg);
            animation: blooming-leaf-right var(--fl-speed) 1.4s backwards;
          }
          
          .flower__line__leaf--3 {
            border-top-right-radius: 0;
            border-bottom-left-radius: 0;
            border-top-left-radius: var(--h);
            border-bottom-right-radius: var(--h);
            left: -460%;
            top: 12%;
            transform: rotate(-70deg) rotateY(30deg);
            animation: blooming-leaf-left var(--fl-speed) 1.2s backwards;
          }
          
          .flower__line__leaf--4 {
            border-top-right-radius: 0;
            border-bottom-left-radius: 0;
            border-top-left-radius: var(--h);
            border-bottom-right-radius: var(--h);
            left: -460%;
            top: 40%;
            transform: rotate(-70deg) rotateY(30deg);
            animation: blooming-leaf-left var(--fl-speed) 1s backwards;
          }
          
          @keyframes grow-from-bottom {
            0% {
              transform: translateY(100%);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes moving-flower-1 {
            0%, 100% {
              transform: translateY(0) rotate(5deg);
            }
            50% {
              transform: translateY(0) rotate(-5deg);
            }
          }
          
          @keyframes moving-flower-2 {
            0%, 100% {
              transform: translateY(0) rotate(20deg);
            }
            50% {
              transform: translateY(0) rotate(12deg);
            }
          }
          
          @keyframes moving-flower-3 {
            0%, 100% {
              transform: translateY(0) rotate(-20deg);
            }
            50% {
              transform: translateY(0) rotate(-25deg) rotateY(-15deg);
            }
          }
          
          @keyframes blooming-leaf-right {
            0% {
              transform-origin: left;
              transform: rotate(70deg) rotateY(30deg) scale(0);
            }
          }
          
          @keyframes blooming-leaf-left {
            0% {
              transform-origin: right;
              transform: rotate(-70deg) rotateY(30deg) scale(0);
            }
          }
          
          @keyframes grow-flower-tree {
            0% {
              height: 0;
              border-radius: 1px;
            }
          }
          
          @keyframes blooming-flower {
            0% {
              transform: scale(0);
            }
          }
          
          @keyframes fade-in {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 0.8;
            }
          }
          
          /* Small particles for flowers */
          .flower__particle {
            position: absolute;
            width: 3px;
            height: 3px;
            background-color: #F9D423;
            border-radius: 50%;
            filter: blur(0.5px);
            opacity: 0.8;
            animation: particle-float 3s ease-in-out infinite;
          }
          
          .flower__particle--1 {
            left: -2px;
            bottom: 5px;
            animation-delay: 0.3s;
          }
          
          .flower__particle--2 {
            left: 4px;
            bottom: 3px;
            animation-delay: 0.7s;
          }
          
          .flower__particle--3 {
            left: 1px;
            bottom: 7px;
            animation-delay: 1.1s;
          }
          
          @keyframes particle-float {
            0% {
              transform: translateY(0px);
              opacity: 0;
            }
            20% {
              opacity: 0.8;
            }
            100% {
              transform: translateY(-20px);
              opacity: 0;
            }
          }
        `
      }} />
    </footer>
  );
};